import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import {
  IServiceData,
  IServiceTranslationContent,
} from '@/types/service-management.types';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import { useTranslateServiceMutation } from '@/services/ServiceManagementService';
import { enqueueSnackbar } from 'notistack';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TranslationMap = Record<string, IServiceTranslationContent>;

interface PreviewModalProps extends Omit<ModalProps, 'children'> {
  formData: Partial<IServiceData>;
  source_language?: string;
  /**
   * Pre-existing translations from the API / parent form state.
   * When provided the modal uses these directly and skips the auto-fetch.
   * Only the "Regenerate" button will call the translate API.
   */
  initialTranslations?: TranslationMap;
  onTranslationsUpdate?: (translations: TranslationMap) => void;
  /**
   * Called when the user edits the source-language card so the parent form
   * stays in sync.
   */
  onSourceFormDataChange?: (fields: {
    name?: string;
    description?: string;
  }) => void;
}

interface LanguagePreviewProps {
  language: { code: string; label: string; flag: string };
  /** Whether this card is the source language card */
  isSource: boolean;
  content: { name?: string; description?: string };
  isLoading: boolean;
  onEditContent?: (
    languageCode: string,
    field: 'name' | 'description',
    value: string
  ) => void;
  /** Called when user edits the source card */
  onSourceChange?: (fields: { name?: string; description?: string }) => void;
}

// ---------------------------------------------------------------------------
// Per-language field labels
// ---------------------------------------------------------------------------
const FIELD_LABELS: Record<string, { name: string; description: string }> = {
  en: { name: 'Name', description: 'Description' },
  de: { name: 'Name', description: 'Beschreibung' },
  fi: { name: 'Nimi', description: 'Kuvaus' },
  sv: { name: 'Namn', description: 'Beskrivning' },
};

const getFieldLabels = (code: string) => FIELD_LABELS[code] ?? FIELD_LABELS.en;

// ---------------------------------------------------------------------------
// LanguagePreview — renders one language card
// ---------------------------------------------------------------------------
const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  isSource,
  content,
  isLoading,
  onEditContent,
  onSourceChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<{
    name?: string;
    description?: string;
  }>({});

  const fieldLabels = getFieldLabels(language.code);

  const toggleEditMode = () => {
    if (!isEditing) {
      setEditValues({
        name: content.name ?? '',
        description: content.description ?? '',
      });
    } else {
      setEditValues({});
    }
    setIsEditing((v) => !v);
  };

  const handleChange = (field: 'name' | 'description', value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
    // Real-time sync for source language
    if (isSource && onSourceChange) {
      onSourceChange({ [field]: value });
    }
  };

  const handleSave = () => {
    if (isSource) {
      if (onSourceChange) onSourceChange(editValues);
    } else if (onEditContent) {
      if (editValues.name !== undefined)
        onEditContent(language.code, 'name', editValues.name);
      if (editValues.description !== undefined)
        onEditContent(language.code, 'description', editValues.description);
    }
    // Always exit edit mode
    setIsEditing(false);
    setEditValues({});
  };

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      {/* Card header */}
      <div className="bg-gray-100 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={`${language.label} flag`}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h2 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
            {isSource && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Source
              </span>
            )}
            {isLoading && (
              <span
                className="ml-2 flex items-center text-xs text-blue-600 font-medium"
                role="status"
                aria-live="polite"
              >
                <svg
                  className="animate-spin h-3 w-3 mr-1"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                translating…
              </span>
            )}
          </h2>
        </div>

        {/* Edit / Save / Cancel buttons — hidden while loading */}
        {!isLoading && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditValues({});
                  }}
                  className="text-gray-600 hover:text-gray-800 p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm"
                  aria-label={`Cancel editing ${language.label} translation`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 flex items-center text-sm"
                  aria-label={`Save ${language.label} translation`}
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Save
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={toggleEditMode}
                className="bg-gray-100 text-blue-600 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50 transition-colors"
                title="Edit translation"
                aria-label={`Edit ${language.label} translation`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Card body */}
      {isLoading ? (
        <div
          className="p-5 flex justify-center"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <span className="sr-only">Loading translation</span>
          <div className="animate-pulse flex space-x-4 w-full">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded w-5/6" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-5 space-y-4">
          {/* Name */}
          <div>
            <span className="block text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
              {fieldLabels.name}
            </span>
            {isEditing && isSource ? (
              <input
                id={`name-${language.code}`}
                type="text"
                value={editValues.name ?? ''}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full p-2 bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                placeholder="Enter name"
                aria-label={`${fieldLabels.name} in ${language.label}`}
              />
            ) : (
              <p
                className={clsx(
                  'text-base font-medium text-gray-800',
                  !content.name && 'text-gray-400 italic'
                )}
              >
                {content.name || '—'}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <span className="block text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
              {fieldLabels.description}
            </span>
            {isEditing ? (
              <textarea
                id={`description-${language.code}`}
                value={editValues.description ?? ''}
                onChange={(e) => handleChange('description', e.target.value)}
                className="w-full p-2 bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[100px]"
                placeholder="Enter description"
                aria-label={`${fieldLabels.description} in ${language.label}`}
              />
            ) : (
              <p
                className={clsx(
                  'text-base text-gray-700 whitespace-pre-wrap leading-relaxed',
                  !content.description && 'text-gray-400 italic'
                )}
              >
                {content.description || '—'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// PreviewModal
// ---------------------------------------------------------------------------
const PreviewModal: React.FC<PreviewModalProps> = ({
  formData,
  source_language = 'en',
  initialTranslations,
  onTranslationsUpdate,
  onSourceFormDataChange,
  open,
  onClose,
  className,
}) => {
  // ── Translation state ────────────────────────────────────────────────────
  // Prefer initialTranslations (edit mode / already-generated) over
  // formData.translations so the parent controls the starting point.
  const [translations, setTranslations] = useState<TranslationMap>(
    () =>
      initialTranslations ??
      (formData.translations as TranslationMap | undefined) ??
      {}
  );

  // Stable refs to avoid stale closures inside the regenerate callback
  const currentFormDataRef = useRef(formData);
  const currentSourceLangRef = useRef(source_language);
  const currentCallbackRef = useRef(onTranslationsUpdate);

  useEffect(() => {
    currentFormDataRef.current = formData;
    currentSourceLangRef.current = source_language;
    currentCallbackRef.current = onTranslationsUpdate;
  }, [formData, source_language, onTranslationsUpdate]);

  // Sync when the parent updates initialTranslations (e.g. after re-opening in edit mode)
  useEffect(() => {
    if (initialTranslations && Object.keys(initialTranslations).length > 0) {
      setTranslations(initialTranslations);
    }
  }, [initialTranslations]);

  // Reset loading when modal closes
  const [translateService, { isLoading: isTranslating }] =
    useTranslateServiceMutation();

  useEffect(() => {
    if (!open) return;
    // no auto-fetch — user clicks "Regenerate" explicitly
  }, [open]);

  // This useMemo is used for rendering — it must depend on translations + formData fields
  const languageContents = useMemo(() => {
    return I18N_LANGUAGES.map((lang) => ({
      lang,
      content:
        lang.code === source_language
          ? { name: formData.name, description: formData.description }
          : { name: formData.name, description: translations[lang.code]?.description ?? '' },
    }));
  }, [source_language, formData.name, formData.description, translations]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleRegenerateTranslations = useCallback(async () => {
    const fd = currentFormDataRef.current;
    if (!fd.description) {
      enqueueSnackbar('Please provide a service description to translate', {
        variant: 'warning',
      });
      return;
    }

    try {
      const response = await translateService({
        description: fd.description ?? '',
        source_language: currentSourceLangRef.current,
      }).unwrap();

      if (response.warning_message) {
        enqueueSnackbar(response.warning_message, {
          variant: 'warning',
        });
      } else if (response.translations) {
        const result = response.translations as TranslationMap;
        setTranslations(result);
        currentCallbackRef.current?.(result);
        enqueueSnackbar('Translations regenerated successfully', {
          variant: 'success',
        });
      }
    } catch (error) {
      console.error('Translation failed:', error);
      enqueueSnackbar('Failed to generate translations. Please try again.', {
        variant: 'error',
      });
    }
  }, [translateService]);

  const handleEditContent = useCallback(
    (languageCode: string, field: 'name' | 'description', value: string) => {
      setTranslations((prev) => ({
        ...prev,
        [languageCode]: {
          ...(prev[languageCode] ?? {}),
          [field]: value,
        },
      }));
    },
    []
  );

  const handleSourceChange = useCallback(
    (fields: { name?: string; description?: string }) => {
      if (onSourceFormDataChange) {
        onSourceFormDataChange(fields);
      }
    },
    [onSourceFormDataChange]
  );

  const handleApplyTranslations = useCallback(() => {
    // Only pass through non-source language entries (source language is in formData)
    const cleaned: TranslationMap = Object.fromEntries(
      Object.entries(translations)
        .filter(([key]) => key !== source_language && !key.includes('_'))
        .map(([lang, content]) => [
          lang,
          {
            name: formData.name || '',
            description: content.description ?? '',
            ...(content.key_box_pickup_instructions !== undefined
              ? {
                  key_box_pickup_instructions:
                    content.key_box_pickup_instructions,
                }
              : {}),
            ...(content.key_box_return_instructions !== undefined
              ? {
                  key_box_return_instructions:
                    content.key_box_return_instructions,
                }
              : {}),
          },
        ])
    );

    if (onTranslationsUpdate) {
      onTranslationsUpdate(cleaned);
    }
    enqueueSnackbar('Translations applied successfully!', {
      variant: 'success',
    });
    onClose?.({}, 'backdropClick');
  }, [translations, source_language, onTranslationsUpdate, onClose, formData.name]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="preview-modal-title"
      aria-describedby="preview-modal-description"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-y-auto max-h-[90vh] custom-scrollbar"
        role="document"
      >
        {/* Header */}
        <div className="bg-gray-100 px-6 py-4 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <h1
              id="preview-modal-title"
              className="text-xl font-bold text-gray-800 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              Service Translation Preview
            </h1>

            <div className="flex items-center gap-2">
              {/* Regenerate button — only way to call the translate API */}
              <button
                type="button"
                onClick={handleRegenerateTranslations}
                disabled={isTranslating}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                aria-label="Regenerate translations from current name and description"
              >
                {isTranslating ? (
                  <>
                    <svg
                      className="animate-spin h-3 w-3 mr-1"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Translating…
                  </>
                ) : (
                  'Regenerate Translations'
                )}
              </button>

              {/* Close X */}
              <button
                type="button"
                onClick={() => onClose?.({}, 'backdropClick')}
                className="text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close translation preview modal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p
            id="preview-modal-description"
            className="text-sm text-gray-500 mb-4 flex items-center"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Review and edit translations. The source card shows what you typed
            in the form — editing it syncs back to the form.
          </p>

          <div
            className="pr-2 custom-scrollbar"
            role="region"
            aria-label="Translation previews"
          >
            {languageContents.map(({ lang, content }) => (
              <LanguagePreview
                key={lang.code}
                language={lang}
                isSource={lang.code === source_language}
                content={content}
                isLoading={isTranslating}
                onEditContent={handleEditContent}
                onSourceChange={handleSourceChange}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-100 border-t border-gray-100">
          <div
            className="flex justify-end gap-3"
            role="group"
            aria-label="Modal actions"
          >
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
              aria-label="Cancel and close modal"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyTranslations}
              disabled={isTranslating}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors duration-200 disabled:opacity-50"
              aria-label="Apply translations to service"
            >
              Apply Translations
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PreviewModal;
